// ** React Imports
import { Suspense } from 'react'

// ** Third Party Imports
import { Navigate, type RouteObject } from 'react-router-dom'

// ** Utility Imports
import { lazyImport } from '@/shared/lib'

// ** Component Imports
// Uncomment the lines below after the launch phase
// import { BlankLayout } from '@/shared/ui'

// ** Layout Imports
// Layout and pages are lazy so each route only downloads its own code
// (an eager import here puts its whole dependency tree in the entry chunk)
import { FallbackLoader } from '@/shared/ui/elements/FallbackLoader'
import { NoIndex, RedirectTo } from './routeHelpers'

const { LandingPageLayout } = lazyImport(
	async () => await import('@/app/layouts/LandingPageLayout'),
	'LandingPageLayout'
)
const { SupportPage } = lazyImport(
	async () =>
		await import('@/pages/support').then((module) => ({
			SupportPage: module.default
		})),
	'SupportPage'
)

// ** Route Imports
// Public Routes
/**
 * Uncomment the lines below after the launch phase
 * This enables only home route to be available during the launch phase
 */
// const { LoginPage } = lazyImport(
//  async () => await import('@/pages/login'),
//  'LoginPage'
// )
// const { RegisterPage } = lazyImport(
//  async () => await import('@/pages/register'),
//  'RegisterPage'
// )
// const { ForgotPasswordPage } = lazyImport(
//  async () => await import('@/pages/forgot-password'),
//  'ForgotPasswordPage'
// )
// const { NewPasswordPage } = lazyImport(
//  async () => await import('@/pages/new-password'),
//  'NewPasswordPage'
// )
// Landing Page Routes
const { HomePage } = lazyImport(
	async () => await import('@/pages/home'),
	'HomePage'
)
const { HomePageNew } = lazyImport(
	async () => await import('@/pages/home-new'),
	'HomePageNew'
)

const { AboutPage } = lazyImport(
	async () => await import('@/pages/about'),
	'AboutPage'
)
const { AboutPageNew } = lazyImport(
	async () => await import('@/pages/about-new'),
	'AboutPageNew'
)
const { FaqPage } = lazyImport(
	async () => await import('@/pages/faq'),
	'FaqPage'
)
const { ContactPage } = lazyImport(
	async () => await import('@/pages/contact'),
	'ContactPage'
)
const { PrivacyPolicyPage } = lazyImport(
	async () => await import('@/pages/privacy-policy'),
	'PrivacyPolicyPage'
)
const { PrivacyPolicyPageNew } = lazyImport(
	async () => await import('@/pages/privacy-policy-new'),
	'PrivacyPolicyPageNew'
)
const { TermsAndConditionsPage } = lazyImport(
	async () => await import('@/pages/terms-and-conditions'),
	'TermsAndConditionsPage'
)
const { NewHomePage } = lazyImport(
	async () => await import('@/pages/new-home'),
	'NewHomePage'
)

export const publicRoutes: RouteObject[] = [
	// Home page (redesign), standalone: own nav/footer, no LandingPageLayout
	{
		path: '/home',
		element: (
			<Suspense fallback={<FallbackLoader />}>
				<NewHomePage />
			</Suspense>
		)
	},
	// Root and the preview URL -> /home (keeps ?query and #hash)
	{
		path: '/',
		element: <RedirectTo to='/home' />
	},
	{
		path: '/new-home',
		element: <RedirectTo to='/home' />
	},
	/**
	 * Uncomment the lines below after the launch phase
	 * This enables only home route to be available during the launch phase
	 */
	// {
	//  element: <BlankLayout />,
	//  children: [
	//    {
	//      path: '/login',
	//      element: <LoginPage />
	//    },
	//    {
	//      path: '/register',
	//      element: <RegisterPage />
	//    },
	//    {
	//      path: '/forgot-password',
	//      element: <ForgotPasswordPage />
	//    },
	//    {
	//      path: '/new-password',
	//      element: <NewPasswordPage />
	//    }
	//  ]
	// },
	{
		element: (
			<Suspense fallback={<FallbackLoader />}>
				<LandingPageLayout />
			</Suspense>
		),
		children: [
			// Previous home page, kept for rollback: not linked, not indexed
			{
				path: '/legacy-home',
				element: (
					<NoIndex>
						<HomePageNew />
					</NoIndex>
				)
			},
			{
				path: '/about',
				element: <AboutPageNew />
			},
			{
				path: '/privacy-policy',
				element: <PrivacyPolicyPageNew />
			},
			{
				path: '/support',
				element: <SupportPage />
			}
			// new landing page does not have the following routes
			// {
			//  path: '/about',
			//  element: <AboutPage />
			// },
			// {
			//  path: '/faq',
			//  element: <FaqPage />
			// },
			// {
			//  path: '/contact',
			//  element: <ContactPage />
			// },
			// {
			//  path: '/privacy-policy',
			//  element: <PrivacyPolicyPage />
			// },
			// {
			//  path: '/terms-conditions',
			//  element: <TermsAndConditionsPage />
			// }
		]
	},
	{
		path: '*',
		element: <Navigate to='/home' replace />
		/**
		 * Uncomment the line below and delete the line above after the launch phase
		 * This enables only home route to be available during the launch phase
		 */
		// element: <Navigate to='/login' replace />
	}
]
